import {Component, OnInit, inject} from '@angular/core';
import {DataService} from '../../../services/data.service';
import {AccessService} from '../../../services/access.service';
import {ProjectStatus} from "../../interfaces";
import {LocalStorageService} from "../../../services/local-storage.service";
import moment from 'moment';
import {NgIf, NgFor, NgClass, DatePipe, KeyValuePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {FormsModule} from '@angular/forms';
import {DebounceKeyupDirective} from '../../../directives/debounce-keyup.directive';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {ProjectStatusPipe} from '../../../pipes/project-status.pipe';

@Component({
    selector: 'app-research-list',
    templateUrl: './research-list.component.html',
    styleUrl: './research-list.component.scss',
    imports: [NgIf, RouterLink, PaginationModule, FormsModule, DebounceKeyupDirective, PopoverModule, NgFor, NgClass, DatePipe, KeyValuePipe, ProjectStatusPipe]
})
export class ResearchListComponent implements OnInit {

    accessService = inject(AccessService);
    private dataService = inject(DataService);
    private localStorageService = inject(LocalStorageService);

    paginConfig = {
        itemsPerPage: 25,
        page: 1
    };
    filter = {
        order: 'number',
        reverse: true,
        showPast: false,

        number: '',
        status: [],
        title: '',
        id_department: 'all',
        id_region: 'all',
        id_country: 'all',
        id_lead: 'all',
        content: 'all',
        time: 'all',
        costs: 'all',
        milestones: 'all',
    };

    leads: any[] = [];
    regions: any[] = [];
    departments: any[] = [];
    countries: any[] = [];
    projects: any[] = [];
    stats: any;

    // set initially to a high value to we can set the filter page, else it would always set the current page to 1
    totalProjects = 1000;
    ProjectStatus = ProjectStatus;

    exporting = false;

    highlightedRow = null;

    ngOnInit(): void {
        this.dataService.request("Country/listForDashboard").subscribe(response => {
            this.countries = response.countries;
        })

        this.dataService.request("Department/listForProject").subscribe(response => {
            this.departments = response.departments;
        })

        this.dataService.request("Region/listForProject").subscribe(response => {
            this.regions = response.regions;
        })

        this.dataService.request("User/listForDashboard").subscribe(response => {
            this.leads = response.users;
        })

        this.highlightedRow = this.localStorageService.getItem('research-highlighted-row');

        this.filter = this.localStorageService.getItem('research-filter');
        if (!this.filter) {
            this.resetFilter();
        }
        this.loadProjects(this.paginConfig);
    }

    /*
     * This is used to override the default comparator function of keyvalue pipe in angular - https://angular.io/api/common/KeyValuePipe
     */
    asIsOrder() {
        return 0;
    }

    setFilterStatus(status): void {
        this.resetFilter();
        this.filter.status = status;
        this.loadProjects(this.paginConfig);
    }

    resetFilter(): void {
        this.filter = {
            order: 'number',
            reverse: true,
            showPast: false,

            number: '',
            status: [],
            title: '',
            id_department: 'all',
            id_region: 'all',
            id_country: 'all',
            id_lead: 'all',
            content: 'all',
            time: 'all',
            costs: 'all',
            milestones: 'all',
        };

        this.paginConfig.page = 1;
    }

    resetFilterAndReload(): void {
        this.resetFilter();
        this.loadProjects(this.paginConfig);
    }

    setOrder(order): void {
        this.filter.order = order;
        this.filter.reverse = !this.filter.reverse;
        this.loadProjects(this.paginConfig);
    }

    loadProjects(event): void {
        this.localStorageService.setItem('research-filter', this.filter);

        this.paginConfig = event;

        this.projects = [];
        this.dataService.request('Research/listAll', {
            filter: this.filter,
            pagination: this.paginConfig
        }).subscribe(response => {
            this.projects = response.projects;
            this.totalProjects = parseInt(response.totalProjects, 10);
            this.stats = response.stats;

            // iterate stats object and parse values as number
            for (const key in this.stats) {
                this.stats[key] = parseInt(this.stats[key], 10);
            }

            // set default values for stats
            this.stats.created = this.stats.created ?? 0;
            this.stats.in_progress = this.stats.in_progress ?? 0;
            this.stats.released = this.stats.released ?? 0;
            this.stats.in_elaboration = this.stats.in_elaboration ?? 0;
            this.stats.evaluation = this.stats.evaluation ?? 0;
            this.stats.revise = this.stats.revise ?? 0;
            this.stats.completion_filed = this.stats.completion_filed ?? 0;

            this.projects.forEach(project => {
                // highlight missing status
                if (project.status == ProjectStatus.IN_PROGRESS) {
                    if (!project.status_report) {
                        project.status_missing = true;
                    } else {
                        project.status_missing = moment(project.status_report.created_at).isBefore(moment(), 'month');
                    }
                } else {
                    project.status_missing = false;
                }
            })
        });
    }

    exportProjects(): void {
        this.exporting = true;
        this.dataService.request("Export/research", {
            filter: this.filter
        }).subscribe(response => {
            this.exporting = false;
            const link = document.createElement('a');

            link.setAttribute('href', 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + response.export.data);
            link.setAttribute('download', response.export.filename);
            link.click();
        });
    }

    highlightRow(id): void {
        this.highlightedRow = id;

        this.localStorageService.setItem('research-highlighted-row', id);
    }
}
